import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {Distribution} from "#material/nodes/shader/bsdf-glossy/types"
import {distributionSchema} from "#material/nodes/shader/bsdf-glossy/types"

export interface BsdfGlossySockets extends MaterialNodeSockets {
    inputs: {
        anisotropy: "float"
        color: "color" // found in inputs: color; found in settings: color; inferred from data: float3
        normal: "normal" // found in inputs: normal; found in settings: vector; inferred from data: float3
        rotation: "float"
        roughness: "float" // found in inputs: color; found in settings: float; inferred from data: float
        tangent: "vector"
    }
    settings: {
        anisotropy: "float"
        color: "color" // found in inputs: color; found in settings: color; inferred from data: float3
        normal: "normal" // found in inputs: normal; found in settings: vector; inferred from data: float3
        rotation: "float"
        roughness: "float" // found in inputs: color; found in settings: float; inferred from data: float
        tangent: "vector"
    }
    settingsExtensions: {
        distribution?: Distribution // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        bsdf: "closure"
    }
}

// neither of these shapes anything while the highlight is round
const HAS_ANISOTROPY = {setting: "anisotropy", whenSet: true} as const

export const bsdfGlossyDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeBsdfGlossy,
    },
    nodeClass: "BsdfGlossy",
    ui: {
        label: "Glossy BSDF",
        color: "#2d8a5d",
        items: [
            {name: "bsdf", valueType: "closure", label: "BSDF", output: true},
            {name: "color", label: "Color", valueType: "color", input: true, setting: true, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "roughness", label: "Roughness", valueType: "float", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0.5},
            {name: "anisotropy", label: "Anisotropy", valueType: "float", input: true, range: {min: -1, max: 1}, setting: true, defaultValue: 0},
            {
                name: "rotation",
                label: "Rotation",
                valueType: "float",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0,
                visibleWhen: HAS_ANISOTROPY,
            },
            {name: "normal", label: "Normal", valueType: "normal", input: true, setting: false},
            {name: "tangent", label: "Tangent", valueType: "vector", input: true, setting: false, visibleWhen: HAS_ANISOTROPY},
            {
                name: "distribution",
                defaultValue: "GGX",
                label: "Distribution",
                valueType: "string",
                options: [
                    {label: "Beckmann", value: "BECKMANN"},
                    {label: "GGX", value: "GGX"},
                    {label: "Ashikhmin-Shirley", value: "ASHIKHMIN_SHIRLEY"},
                    {label: "Multiscatter GGX", value: "MULTI_GGX"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({
            bsdf: materialClosureOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                anisotropy: optionalMaterialInput,
                color: optionalMaterialInput,
                normal: optionalMaterialInput,
                rotation: optionalMaterialInput,
                roughness: optionalMaterialInput,
                tangent: optionalMaterialInput,
            }),
            settings: z.strictObject({
                anisotropy: z.optional(z.number()),
                color: z.optional(color),
                roughness: z.optional(z.number()),
                normal: z.optional(vec3),
                rotation: z.optional(z.number()),
                tangent: z.optional(vec3),
                distribution: z.optional(distributionSchema),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<BsdfGlossySockets>
